import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { Medication, medicationService } from '../../services/medicationService';
import { datePickerToCalendarValue } from '../../utils/dateUtils';
import { executeUpdate, OfflineWriteBlockedError } from '../../offline';
import QuantityField from '../QuantityField';

/**
 * Seed a `<DatePicker>` from a stored CALENDAR DAY: the date PART, at the
 * browser's own midnight, so the picker opens on the day the chart shows.
 *
 * The READ half of the W10 rollback, and the half that actually loses data.
 * `new Date(iso)` seeded the picker at the INSTANT, so a UTC-midnight-anchored
 * row — barn ops, an import, iOS — opened on the PREVIOUS day in every US zone,
 * and pressing Save then wrote that previous day back. Editing only the sig was
 * enough to walk a start date backwards one day per edit.
 *
 * The read-side twin of `datePickerToCalendarValue`. It lives here rather than in
 * `dateUtils.ts` so this page's branch does not touch the shared helper module.
 *
 * ⚠ `Medication.startDate` / `endDate` USED TO BE MIXED at the COLUMN level
 * (DATE-KINDS.md §1.2 — SOAP auto-prescribe stamped `existingNote.date`, a real
 * instant, into the same column). That writer is collapsed as of this branch:
 * `soap-notes.service.ts` now anchors every medication it creates at noon UTC on
 * the CLINIC's day, so every NEW row is a calendar day and the date PART is the
 * whole truth.
 *
 * RESIDUAL, and it is a real one: rows written by the OLD SOAP writer still hold
 * an instant. For a note completed after 19:00 Eastern / 16:00 Pacific that
 * instant's UTC date part is TOMORROW, so this seeds — and the card renders —
 * one day late for those rows until they are re-anchored. Nothing here can tell
 * the two apart, which is exactly why the fix is on the write side; the
 * containment is that `handleSave` posts a date only when the picker was
 * actually CHANGED, so editing a sig can no longer persist the wrong day.
 * Tracked in DATE-KINDS.md §2.2.
 */
export function calendarValueToPickerDate(iso?: string | null): Date | null {
  if (typeof iso !== 'string') return null;
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const d = parseISO(day);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The calendar-date half of the update payload: a picker is posted ONLY when it
 * differs from what it was seeded with.
 *
 * Why "changed", not "present". The seed is a date PART, and for a row this form
 * cannot faithfully represent — a legacy SOAP-written INSTANT, see
 * `calendarValueToPickerDate` — that part can be the day AFTER the day the row
 * means. Posting it on every save would re-date the row on an edit that only
 * touched the sig, one day per edit, invisibly. An untouched field is left out,
 * which the server reads as "leave unchanged".
 *
 * A CLEARED picker is also left out, deliberately: `datePickerToCalendarValue`
 * returns undefined for it and the server has no "unset this date" verb, so
 * clearing was never possible here and this does not pretend otherwise.
 */
export function changedCalendarDates(
  current: { start: Date | null; end: Date | null },
  seeded: { start?: string; end?: string },
): { startDate?: string; endDate?: string } {
  const out: { startDate?: string; endDate?: string } = {};
  const nextStart = datePickerToCalendarValue(current.start);
  const nextEnd = datePickerToCalendarValue(current.end);
  if (nextStart !== undefined && nextStart !== seeded.start) out.startDate = nextStart;
  if (nextEnd !== undefined && nextEnd !== seeded.end) out.endDate = nextEnd;
  return out;
}

interface EditMedicationDialogProps {
  open: boolean;
  onClose: () => void;
  medication: Medication;
  horseId?: string;
  onSaved?: () => void;
}

/**
 * Edit the clinical fields of an existing medication record — most importantly
 * the sig (Instructions). Refill/dispense state and DEA schedule are managed
 * through the dispense flow and intentionally not editable here.
 */
const EditMedicationDialog: React.FC<EditMedicationDialogProps> = ({
  open,
  onClose,
  medication,
  horseId,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [dosage, setDosage] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [instructions, setInstructions] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * What the two pickers were SEEDED with, in the exact shape `handleSave`
   * would post. A field whose current value still equals its seed is omitted
   * from the payload, which the server reads as "leave unchanged".
   *
   * Why a dirty check and not just "post what the picker holds": the seed is a
   * date PART, and for a legacy SOAP-written row (an INSTANT — see
   * `calendarValueToPickerDate`) that part can be the day AFTER the day the row
   * means. Posting it unconditionally would re-date the row to the wrong day on
   * a save that only touched the sig — one day per edit, silently. Omitting an
   * untouched field makes an unrelated edit unable to move a date at all.
   */
  const seededDatesRef = useRef<{ start?: string; end?: string }>({});

  // Re-seed the form whenever a different medication is opened for editing.
  useEffect(() => {
    if (!open) return;
    setName(medication.name ?? '');
    setDosage(medication.dosage ?? '');
    setQuantity(medication.quantity ?? 1);
    setInstructions(medication.instructions ?? '');
    const seededStart = calendarValueToPickerDate(medication.startDate);
    const seededEnd = calendarValueToPickerDate(medication.endDate);
    setStartDate(seededStart);
    setEndDate(seededEnd);
    seededDatesRef.current = {
      start: datePickerToCalendarValue(seededStart),
      end: datePickerToCalendarValue(seededEnd),
    };
    setError(null);
  }, [open, medication]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Drug name is required');
      return;
    }
    if (!(quantity > 0)) {
      setError('Enter a quantity');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // CALENDAR pickers — anchored at noon UTC rather than posted as a raw
      // `Date` (which axios would serialise at the BROWSER'S midnight, a
      // fourth anchor for this column: DATE-KINDS.md §2.5 W10), and only when
      // actually changed. See `changedCalendarDates`.
      const updateData = {
        name: name.trim(),
        dosage: dosage.trim() || undefined,
        quantity,
        instructions: instructions.trim() || undefined,
        ...changedCalendarDates({ start: startDate, end: endDate }, seededDatesRef.current),
      };
      await executeUpdate({
        entityType: 'medication',
        table: 'medications',
        entityId: medication.id,
        payload: updateData,
        networkFn: () => medicationService.update(medication.id, updateData),
      });
      queryClient.invalidateQueries('medications');
      if (horseId) queryClient.invalidateQueries(['medications', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
      enqueueSnackbar('Medication updated', { variant: 'success' });
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(
        err instanceof OfflineWriteBlockedError
          ? err.message
          : err?.response?.data?.message || 'Failed to update medication',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Medication</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Drug Name"
                size="small"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Dosage"
                size="small"
                fullWidth
                value={dosage}
                onChange={(e) => setDosage(e.target.value)}
                placeholder="50mg, 10ml, etc."
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              {/* Hundredths (ADR-0038). */}
              <QuantityField
                label="Quantity"
                size="small"
                fullWidth
                value={quantity}
                onChange={(next) => setQuantity(next ?? 0)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Instructions (Sig)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g. Give 1 tablet by mouth twice daily"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMedicationDialog;
